import React, { memo, useCallback, useEffect, useState } from 'react';

import { InView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import _chunk from 'lodash/chunk';
import _isEqual from 'lodash/isEqual';

import { Button, Col, Row } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { InventoryListingPlaceholder as InventoryLoadingPlaceholder } from 'reaxl-listing';

import configureListingTiles from '@/utilities/configureListingTiles';

import { seeMoreButtonClick } from '@/analytics/srp/infiniteScrollAnalytics';

import userDuck from '@/ducks/userDuck.js';

import LazyComponent from '@/components/LazyComponent';
import TreehouseAdPlacement from '@/components/TreehouseAdPlacement';

import useCarfaxScrollDetection from './hooks/useCarfaxScrollDetection';
import { useFeatureConfiguration } from './hooks/useFeatureConfiguration';
import useInfiniteScroll from './hooks/useInfiniteScroll';
import { useSearchResultsData } from './hooks/useSearchResultsData';
import HyundaiBuyOnlineArticles from './HyundaiBuyOnlineArticles';
import InlineFilterPromoCard from './InlineFilterPromoCard';
import InventoryListingContainer from './InventoryListingContainer';
import InventorySpecialListing from './InventorySpecialListing';
import ListingsLinerAds from './ListingsLinerAds';
import MyWalletGridContainer from './MyWalletGridContainer';
import SearchResultsCountAndSortContainer from './SearchResultsCountAndSortContainer';
import SimilarInventoryContainerPlaceholder from './SimilarInventoryContainerPlaceholder';
import SRPDealerDiffModalWrapper from './SRPDealerDiffModalWrapper';
import SRPListingCollection from './SRPListingCollection';
import SupplementalSearchPlaceholder from './SupplementalSearchPlaceholder';
import WalletInputCardContainer from './WalletInputCardContainer';

const InventoryListingPlaceholder = React.forwardRef((props, ref) => (
    <Col
        xs={12}
        sm={6}
        md={4}
    >
        <InventoryLoadingPlaceholder
            ref={ref}
            {...props}
        />
    </Col>
));

const SpotlightCarouselPlaceholder = React.forwardRef((props, ref) => (
    <Col xs={12}>
        <div
            ref={ref}
            className="margin-bottom-5"
            style={{ height: 417, ...props.style }}
            {...props}
        />
    </Col>
));

// lazy
export const PreorderSearchContainer = dynamic(() => import(
    /* webpackChunkName: "PreorderSearchContainer" */
    '@/containers/srp/PreorderSearchContainer'
), {
    ssr: false,
    loading: () => (
        <InventoryListingPlaceholder
            data-cmp="inv-placeholder-preorder"
            key="preorder-inventory-placeholder"
        />
    ),
});
export const SupplementalSearchContainer = dynamic(() => import(
    /* webpackChunkName: "SupplementalSearchContainer" */
    '@/containers/srp/SupplementalSearchContainer'
), {
    ssr: false,
    loading: () => (<SupplementalSearchPlaceholder />),
});
export const SimilarInventoryContainer = dynamic(() => import(
    /* webpackChunkName: "SimilarInventoryContainer" */
    '@/containers/srp/SimilarInventoryContainer'
), {
    ssr: false,
    loading: () => (<SimilarInventoryContainerPlaceholder />),
});
export const SpotlightCarouselContainer = dynamic(() => import(
    /* webpackChunkName: "SpotlightCarouselContainer" */
    '@/containers/srp/SpotlightCarouselContainer'
), {
    ssr: false,
    loading: () => (<SpotlightCarouselPlaceholder />),
});

/*
 * render list of inventory listings as spotlights, regular and supplemental listings
 */
function InventorySearchResultsContainer({ hierarchyData, filterSetParams }) {

    const dispatch = useDispatch();

    // Consolidated data from Redux selectors
    const {
        brandData: { brand, isKbbBranded },
        deviceData: { isLg, isXs, isSm },
        listingsData: { listings, resultCount, isResultsLoading, isMaxActiveResults },
        spotlightData: {
            spotlights,
            spotlightTypes,
            boostListings,
            vehicleHighlightBooListing,
            vehicleHighlightCarouselListings,
        },
        supplementalData: {
            supplementalResultsCount,
            supplementalResultsLoaded,
            supplementalInitialLoad,
            supplementalLoading,
        },
        paginationData: { currentPage, resultsPerPage, isLastPage },
        userData: { isLoggedIn, leadProfile, disableLazyListings },
        filtersData: { zip, searchRadius, isElectricFilter },
        sort,
        query,
        certifiedSponsor,
        isNationalSRP,
        derivedState: { hasSearchRadius, isRelevantSort, showSupplementalResults },
    } = useSearchResultsData();

    const {
        brandConfig: { channel, vhrProviders, relevanceConfig, inventoryImageUrl },
        adConfig: {
            enableFluidAd,
            enableListingCardAd,
            isTreehouseAdEnabled,
            linerAd: { hideLinerAd1, hideLinerAd2, hideLinerAd3, hideLinerAd4, hideLinerAd5 },
        },
        spotlightConfig: {
            enableSpotlightNonRelevantSort,
            carouselRowPlacement,
            peekabooListingPlacement,
            showSpotlightsBeforeMEE,
            showSpotlightsBottomPage,
            hideSpotlights,
            enableVehicleHighlights,
        },
        listingConfig: { hideOwnerName, displayPhoneNumber, enableInlineFilters },
        tileConfig: { isOverrideCarfaxTileEnabled, partner, removeCarfaxTile, removeExperianTile },
        walletConfig: { enableMyWallet, enableNewWalletPromoCard },
        supplementalConfig: { supplementalEnabled, enableSimilarListings },
        infiniteScrollConfig: { defaultThreshold, enableDefaultQueryParams, queryParams },
        boostConfig: { enableNewCarBoost },
    } = useFeatureConfiguration();

    // Feature-flag dependent derived state (needs to be computed here)
    const shouldLoadSimilarListings = !isNationalSRP && hasSearchRadius && enableSimilarListings;

    const [showSimilarListings, setShowSimilarListings] = useState(false);

    useEffect(() => {
        if (userDuck && !leadProfile) {
            dispatch(userDuck.creators.setLeadProfile());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!shouldLoadSimilarListings) {
            return;
        }

        if (showSimilarListings && supplementalResultsCount === 0 && supplementalResultsLoaded === 0 && supplementalInitialLoad) {
            // detect that srpSupplementalDuck has been cleared on SupplementalSearchContainer useEffect
            setShowSimilarListings(false);
        } else if (!showSimilarListings && !supplementalLoading && !supplementalInitialLoad && !isResultsLoading) {
            const isLowResultCount = resultCount + supplementalResultsCount <= 25;
            const supplementalListingsRendered = supplementalResultsCount > 0 && ((supplementalResultsLoaded >= supplementalResultsCount) || supplementalResultsLoaded === 100);
            const noSupplementalLoaded = !supplementalInitialLoad && !supplementalLoading && supplementalResultsCount === 0;
            const isSupplementalDoneLoading = supplementalListingsRendered || noSupplementalLoaded;
            if (enableSimilarListings && isLowResultCount && isSupplementalDoneLoading && hasSearchRadius) {
                setShowSimilarListings(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplementalResultsCount, supplementalResultsLoaded, supplementalInitialLoad, supplementalLoading, resultCount, showSimilarListings, isResultsLoading, isNationalSRP, shouldLoadSimilarListings]);

    // Clean up eventBus and script tag when component unmount
    useEffect(() => {
        ['psx.listingActions.openMakeOffer', 'psx.listingActions.makeOfferModalIsLoaded'].forEach(window.caiEventBus.off);
        document.querySelector('script[src*="static.kbb.com/psx-listing-actions"]')?.remove();
    }, []);

    // Retrieve saved inventory ids and set them in the inventory reducer
    const initializeUserInfo = useCallback(() => {
        dispatch(userDuck.creators.hydrateUserInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    // Retrieve recently viewed inventory ids and set them in the inventory reducer
    const initializeRecentInventory = useCallback(() => {
        dispatch(userDuck.creators.hydrateRecentInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    useEffect(() => {
        initializeRecentInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
      * This useEffect hook is intended to fire only on component mount
      * and will request a fourth spotlight if necessary as well as
      * fire all initial page-loaded spotlight listings
      */
    useEffect(() => {
        // Initialize saved listing and other user data
        // Only call this function on KBB
        if (isKbbBranded) {
            initializeUserInfo();
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listingsWithTiles = configureListingTiles({
        brand,
        certifiedSponsor,
        items: listings,
        isOverrideCarfaxTileEnabled,
        partner,
        tier: 'listing',
        removeCarfaxTile,
        removeExperianTile,
        isXs,
    });

    useCarfaxScrollDetection(isOverrideCarfaxTileEnabled);

    const renderFluidAd = (index) => (
        <Col
            xs={12}
            sm={6}
            md={4}
            className="display-flex justify-content-center"
            key={`fluid-ad-${index}`}
        >
            <div
                className="panel panel-default border-0"
                style={{ marginTop: -17 }}
            >
                <ListingsLinerAds
                    className="test border-0"
                    index={index}
                />
            </div>
        </Col>
    );

    // TODO: maybe just set to invisible instead of unmounting when there are not enough listings?
    const renderLinerAd = (index) => (
        <Col
            xs={12}
            data-cmp={`cntnr-liner-ad-${index}`}
            key={`liner-ad-${index}`}
        >
            <ListingsLinerAds
                index={index}
            />
        </Col>
    );

    // Use custom hook for infinite scroll functionality
    const {
        infiniteScrollButtonPressed,
        isLoadingMore,
        inViewKey,
        handleFetchMoreData,
        handleInViewChange,
        scrollConfig,
    } = useInfiniteScroll({
        isMaxActiveResults,
        listings,
        query,
        featureFlags: {
            enableDefaultQueryParams,
            queryParams,
            enableSpotlightNonRelevantSort,
            hideSpotlights,
            displayPhoneNumber,
            hideOwnerName,
        },
        inventoryImageUrl,
        sort,
        brand,
        channel,
        vhrProviders,
        relevanceConfig,
        zip,
    });

    const searchResultsListings = listingsWithTiles?.map((listing, index) => {
        const paginatedListingIndex = (index + 1) + ((currentPage - 1) * resultsPerPage);
        return (
            <InventoryListingContainer
                key={`inventory-container-${index}`}
                listing={listing}
                index={index}
                paginatedListingIndex={paginatedListingIndex}
            />
        );
    });

    /*
     * Inject 4 Spotlights into the SRP results if available:
     * 3 Premium spotlights between on top of each page/batch
     * 1 Non-Premium spotlight as the 23th listing. Show a Featured over a Standard spotlight if there are the same
     *  or more featured than standard organic listings
     */
    /* eslint-disable no-unused-expressions*/

    // hide spotlights for non-relevant sort
    const shouldShowSpotlights = searchResultsListings.length > 0
        && (!enableSpotlightNonRelevantSort || ((hideSpotlights || enableSpotlightNonRelevantSort) && isRelevantSort));

    if (shouldShowSpotlights) {
        // Helper function to insert spotlight if it exists
        const insertSpotlight = (batchIndex, listingPosition, spotlightType, spotlightIndex, displayIndex) => {
            const spotlight = spotlightTypes?.[batchIndex]?.[spotlightType]?.[spotlightIndex];
            if (spotlight) {
                searchResultsListings.splice(
                    listingPosition,
                    0,
                    <InventorySpecialListing
                        key={`${spotlightType}Spotlight-${displayIndex}`}
                        listingIndex={displayIndex}
                        listingId={spotlight.id}
                    />
                );
            }
        };

        // Process each batch of 30 results
        const batchCount = Math.ceil(searchResultsListings.length / 30);

        for (let batch = 0; batch < batchCount; batch++) {
            const baseIndex = batch * 30;

            // Insert premium spotlights at positions 0, 1, 2
            for (let i = 0; i < 3; i++) {
                if (searchResultsListings.length >= baseIndex + i) {
                    insertSpotlight(batch, baseIndex + i, 'premium', i, baseIndex + i);
                }
            }

            // Insert fourth tier spotlight at position 23
            if (searchResultsListings.length >= baseIndex + 23) {
                insertSpotlight(batch, baseIndex + 23, 'fourthTier', 0, baseIndex + 23);
            }
        }
    }
    // show spotlights at the bottom of the page for non-relevant sort
    if ((showSpotlightsBottomPage || (showSpotlightsBeforeMEE && isLastPage)) && !isRelevantSort) {
        spotlights?.slice(0, 4).forEach((_, index) => {
            searchResultsListings.push(
                <InventorySpecialListing listingIndex={index} />
            );
        });
    }

    /*
    * Inject My Wallet promo grid placement at spot #4
        will be injected in a row separately when higher peekaboo variant is on
    */
    if (enableNewWalletPromoCard) {
        searchResultsListings.splice(3, 0, <WalletInputCardContainer key="WalletInputCardContainer" />);
    }

    /*
     * Inject 2 New Car Boost listings if available
     * first one at index 6 (spot #7) or the first listing in the 3rd row on desktop
     * second at index 21 if there are 75 or more results per page
     */
    if (enableNewCarBoost) {
        if (boostListings[0]) {
            searchResultsListings.splice(6, 0, <InventorySpecialListing
                listingIndex={0}
                listingCategory="boost"
            />);
            // Ensure there is enough listing slots to fill the row before splicing into results
            // We will dispatch an extra listing if there is no NCB listing to serve as a fallback/placeholder
            // To prevent CLS
        }

        if (resultsPerPage >= 75 && resultCount >= 75) {
            boostListings[1] && searchResultsListings.splice(22, 0, <InventorySpecialListing
                listingIndex={1}
                listingCategory="boost"
            />);
        }
    }

    /*
    * Inject Treehouse Ad placement at spot #8, between 7th and 8th listings
    */
    if (isTreehouseAdEnabled && isElectricFilter && searchResultsListings.length >= 8) {
        searchResultsListings.splice(
            7,
            0, (
                <Col
                    className="display-flex"
                    xs={12}
                    sm={6}
                    md={4}
                >
                    <TreehouseAdPlacement
                        key="treehouseAd"
                        isXs={isXs}
                    />
                </Col>
            )
        );
    }

    if (vehicleHighlightBooListing) {
        searchResultsListings.splice(Number(peekabooListingPlacement) || 9, 0, <InventorySpecialListing
            listingIndex={9}
            listingCategory="peekaboo"
        />);
    }

    /*
     * Inject InlineFilterPromoCard placement at spot #8
     */
    if (searchResultsListings.length >= 8 && enableInlineFilters && hierarchyData.hasValidData) {
        searchResultsListings.splice(
            8,
            0,
            (
                <InlineFilterPromoCard
                    key="inlineFilterPromoCard"
                    hierarchyData={hierarchyData}
                    filterSetParams={filterSetParams}
                />
            ),
        );
    }

    // TODO we need a fallback for this once they actually start selling the product enough to matter (or fill with other listings)
    const renderVehicleHighlightCarousel = (
        <LazyComponent
            key="spotlight-carousel-placeholder-3"
            componentClass={SpotlightCarouselPlaceholder}
        >
            {vehicleHighlightCarouselListings.length > 0
                && <SpotlightCarouselContainer spotlights={vehicleHighlightCarouselListings} />}
        </LazyComponent>
    );

    if (enableFluidAd) {
        !hideLinerAd1 && searchResultsListings.length >= 7 && searchResultsListings.splice(7, 0, renderFluidAd(0));
        !hideLinerAd2 && searchResultsListings.length >= 14 && searchResultsListings.splice(14, 0, renderFluidAd(1));
        !hideLinerAd3 && searchResultsListings.length >= 19 && searchResultsListings.splice(19, 0, renderFluidAd(2));
        !hideLinerAd4 && searchResultsListings.length >= 36 && searchResultsListings.splice(36, 0, renderFluidAd(3));
        !hideLinerAd5 && searchResultsListings.length >= 43 && searchResultsListings.splice(43, 0, renderFluidAd(4));
    }

    if (enableListingCardAd) {
        searchResultsListings.length >= 25 && searchResultsListings.splice(25, 0, renderFluidAd(3));
    }

    // wrap every listing after the 3rd one in LazyComponent
    const searchResultsLazyListings = [];
    searchResultsListings.forEach((listing, index) => {
        if (index < 3 || listing?.key === 'myWalletPromoCard' || disableLazyListings) {
            searchResultsLazyListings.push(listing);
        } else {
            searchResultsLazyListings.push(
                <LazyComponent
                    componentClass={InventoryListingPlaceholder}
                    key={`inventory-placeholder-${index}`}
                >
                    {listing}
                </LazyComponent>
            );
        }
    });

    if (isLoadingMore) {
        // Immediately push 25 loading placeholders to the end of the listings when loading more results so that
        // CLS is not impacted when the new results are loaded.
        searchResultsLazyListings.push(
            Array.from({ length: 25 }).map((_, index) => (
                <InventoryListingPlaceholder
                    key={`loading-placeholder-${index}`}
                />
            ))
        );
    }

    // chunk all search results listings in sets of 3 representing each row [[item, item, item], [item, item, item], ...]
    const chunkedListingsSet = _chunk(searchResultsLazyListings, isSm ? 2 : 3);

    // insert 3-column-wide components at specific indexes of chunkedListingsSet
    if (enableVehicleHighlights && searchResultsListings.length > 6) {
        chunkedListingsSet.splice(Number(carouselRowPlacement) || 1, 0, [renderVehicleHighlightCarousel]);
    }

    const linerAdRowNumbers = vehicleHighlightCarouselListings.length > 0 || spotlights.length === 0
        ? [3, 7, 10, 13, 16]
        : [2, 6, 9, 12, 15];

    if (!enableFluidAd) {
        !hideLinerAd1 && searchResultsListings.length > 6 && chunkedListingsSet.splice(linerAdRowNumbers[0], 0, renderLinerAd(0));
        !hideLinerAd2 && searchResultsListings.length > 14 && chunkedListingsSet.splice(linerAdRowNumbers[1], 0, renderLinerAd(1));
        !hideLinerAd3 && searchResultsListings.length > 19 && chunkedListingsSet.splice(linerAdRowNumbers[2], 0, renderLinerAd(2));
        !hideLinerAd4 && searchResultsListings.length > 33 && chunkedListingsSet.splice(linerAdRowNumbers[3], 0, renderLinerAd(3));
        !hideLinerAd5 && searchResultsListings.length > 36 && chunkedListingsSet.splice(linerAdRowNumbers[4], 0, renderLinerAd(4));
    }

    const showWalletGrid = currentPage === 1 || resultCount === 0;
    const renderMyWalletGrid = (enableMyWallet && showWalletGrid) && (
        <MyWalletGridContainer
            uiContext="default"
            showIcon={isLg}
        />
    );

    return (
        <div data-cmp="cntnr-listings">
            <SearchResultsCountAndSortContainer />

            <Row className="display-flex flex-wrap margin-top-3">
                {chunkedListingsSet.slice(0, 1).flat().map((item) => item)}

                {renderMyWalletGrid}

                {chunkedListingsSet.slice(1, 2).flat().map((item) => item)}

                <HyundaiBuyOnlineArticles />

                {chunkedListingsSet.slice(2, chunkedListingsSet.length).flat().map((item) => item)}

                {!infiniteScrollButtonPressed && !isMaxActiveResults && (
                    <Col
                        xs={12}
                        className="margin-vertical-6 text-center"
                    >
                        {!isLoadingMore
                            && (
                                <Button
                                    bsStyle="primary"
                                    bsSize="large"
                                    onClick={async (event) => {
                                        sendClick(seeMoreButtonClick, event, {});
                                        handleFetchMoreData();
                                    }}
                                >
                                    See More Results
                                </Button>
                            )}
                    </Col>
                )}

                {infiniteScrollButtonPressed && !isMaxActiveResults && (
                    <Col
                        xs={12}
                        componentClass={InView}
                        key={`inview-${inViewKey}`}
                        as="div"
                        rootMargin={scrollConfig.rootMargin}
                        threshold={scrollConfig.threshold}
                        triggerOnce={scrollConfig.triggerOnce}
                        onChange={handleInViewChange}
                        className="margin-vertical-5 text-center"
                    >
                        <h3>Loading more results...</h3>
                    </Col>
                )}

                {isLastPage && <PreorderSearchContainer />}

                {isLastPage && supplementalEnabled && (
                    <SupplementalSearchContainer
                        listingsWithTilesLength={listingsWithTiles.length}
                        showSupplementalResults={showSupplementalResults}
                        isLastPage={isLastPage}
                    />
                )}

                {shouldLoadSimilarListings && isLastPage && (showSimilarListings || (!supplementalEnabled && resultCount < 25)) && (
                    <LazyComponent
                        threshold={defaultThreshold}
                        fallback={(<SimilarInventoryContainerPlaceholder />)}
                    >
                        <SimilarInventoryContainer
                            searchRadius={searchRadius}
                            isLastPage={isLastPage}
                        />
                    </LazyComponent>
                )}

                <SRPListingCollection />
            </Row>

            <SRPDealerDiffModalWrapper />

            <lead-generation
                layout="srpListing"
                is-director
            />
        </div>
    );
}

InventorySearchResultsContainer.propTypes = {
    hierarchyData: PropTypes.object.isRequired,
    filterSetParams: PropTypes.object,
};

export default memo(InventorySearchResultsContainer, _isEqual);
